/* eslint-disable no-console */
import type { Ref } from 'vue'
import * as Sentry from '@sentry/browser'
import { useLocalStorage } from '@vueuse/core'
import { acceptHMRUpdate, defineStore } from 'pinia'
import { computed, ref, shallowRef, triggerRef } from 'vue'
import { DateTime } from 'luxon'
import { UTAS_ACTIVE_TERMINALS } from '~~/constants'
import type {
  GETOrganization,
  GETToken,
  GETUserInfo,
  SimpleUser,
} from '~/services/apiClient'
import { useGlobalFilters } from '~/compositions/globalFilters'
import { isUTASOnlyOrg as orgIsUTASOnly } from '~/logic/organizations'
import {
  ACKNOWLEDGED_POPUPS_KEY,
  isInternalUser,
  LA_LB_TERMINALS,
  NEW_FEATURE_CONFIRMED_KEY_PREFIX,
  OAKLAND_TERMINALS,
  TERMINALS_REQUIRING_APPOINTMENTS,
  TERMINALS_WITH_GREEN_LANE,
} from '~/constants'
import {
  broadcastOrgChangeToOtherTabs,
  clearOrgChangeState,
  setOrgChangeState,
} from '~/managers/orgChangeManagement'
import { Organization } from '~/models/organization'
import { UserInfo } from '~/models/userInfo'
import { startOpenReplayTracking } from '~/observability/openReplay'
import * as authRepo from '~/repositories/authentication'
import {
  AUTH_HEADER_KEY,
  NEW_AUTH_HEADER_KEY,
} from '~/repositories/authentication'
import * as userInfoRepo from '~/repositories/userInfo'
import {
  OrganizationSubscription,
  PortComplex,
  UserRoleType,
  UsersApi,
} from '~/services/apiClient'
import {
  clearAxiosAuthorizationHeader,
  getAxiosAuthorizationHeader,
  setAxiosAuthorizationHeader,
} from '~/services/apiHelpers'
import { clearChatData, configureUserWithCrispChat } from '~/services/crispChat'
import {
  HIDE_EXTENSION_POPUP_KEY,
  SHOW_LATER_KEY,
} from '~/stores/extensionPopup'
import { fastParseISO, reloadPage } from '~/utils'
import { useConnectionSetupStatus } from '~/services/connectionSetupStatus'

function isNewFeatureConfirmedKey(key: string): boolean {
  return key.startsWith(NEW_FEATURE_CONFIRMED_KEY_PREFIX)
}

function clearLocalStorageExcept(keepKeys: Set<string>) {
  const allKeys = Object.keys(localStorage)
  allKeys.forEach((key) => {
    if (!keepKeys.has(key) && !isNewFeatureConfirmedKey(key)) {
      localStorage.removeItem(key)
    }
  })
}

const AUTH_HEADERS_BY_ORG_ID_KEY = 'auth_headers_by_org_id'
const EXPORT_IN_TRINIUM_MODE_KEY = 'export_in_trinium_mode'
const EXPORT_ALL_APPOINTMENTS_KEY = 'export_all_appointments'
const USE_OWN_CHASSIS_KEY = 'use_own_chassis'
const DEMO_MODE_KEY = 'demo_mode'
const TEST_MODE_KEY = 'test_mode'
const PORT_COMPLEXES_FILTER_KEY = 'port_complexes_filter'
const ADD_CONTAINERS_PORT_SELECTOR_LAST_USED_VALUE_KEY =
  'add_containers_port_selector_last_used_value'
const ALL_USER_DATA_KEYS = new Set([
  AUTH_HEADERS_BY_ORG_ID_KEY,
  EXPORT_IN_TRINIUM_MODE_KEY,
  EXPORT_ALL_APPOINTMENTS_KEY,
  USE_OWN_CHASSIS_KEY,
  DEMO_MODE_KEY,
  TEST_MODE_KEY,
  AUTH_HEADER_KEY,
  NEW_AUTH_HEADER_KEY,
  HIDE_EXTENSION_POPUP_KEY,
  SHOW_LATER_KEY,
  PORT_COMPLEXES_FILTER_KEY,
  ADD_CONTAINERS_PORT_SELECTOR_LAST_USED_VALUE_KEY,
  // Popup acknowledgements are keyed per user id inside a single shared key
  // and must survive org switches
  ACKNOWLEDGED_POPUPS_KEY,
])

// Keys that should persist across logout (global user preferences)
const GLOBAL_PREFERENCE_KEYS = new Set([
  HIDE_EXTENSION_POPUP_KEY,
  SHOW_LATER_KEY,
  // Per-user-id popup acknowledgements deliberately survive logout so users
  // aren't re-prompted after logging back in
  ACKNOWLEDGED_POPUPS_KEY,
])

export const useUserStore = defineStore('user', () => {
  const authHeader = ref(null as null | string)
  const loggedIn = ref(false)
  const readyToConnectToWebSocket = ref(false)
  const authHeadersByOrgID = useLocalStorage<Record<string, string>>(
    AUTH_HEADERS_BY_ORG_ID_KEY,
    {}
  )
  const exportInTriniumMode = useLocalStorage<boolean>(
    EXPORT_IN_TRINIUM_MODE_KEY,
    false
  )
  const exportAllAppointments = useLocalStorage<boolean>(
    EXPORT_ALL_APPOINTMENTS_KEY,
    false
  )
  const orgNotificationsChannel = ref<string>('')
  const userNotificationsChannel = ref<string>('')

  const useOwnChassisLastUsedValue = useLocalStorage<boolean>(
    USE_OWN_CHASSIS_KEY,
    false
  )
  const loadInfoPromise = ref<Promise<UserInfo> | null>(null)

  const feature_flags = ref(new Set<string>())
  const showHideImportAppointments: Ref<boolean | undefined> = ref(true)

  const demo_mode = useLocalStorage<boolean>(DEMO_MODE_KEY, false, {})
  const testMode = useLocalStorage<boolean>(TEST_MODE_KEY, false)
  const portComplexesLocalStorage = useLocalStorage<string | undefined>(
    PORT_COMPLEXES_FILTER_KEY,
    undefined
  )
  const { connectionSetupStatus } = useConnectionSetupStatus()
  const userInfo = shallowRef(null as null | UserInfo)
  const currentOrg = shallowRef(null as null | Organization)

  // Computed properties
  const loading = computed(() => {
    return !!loadInfoPromise.value
  })

  /**
   * Whether we know *who* is logged in, not merely that someone is.
   *
   * `setAuthHeader()` flips `loggedIn` synchronously while `userInfo` arrives
   * 1–2 seconds later, and every org-shaped predicate below reads from
   * `userInfo`/`currentOrg` — so in that window `is_internal_user` is `false`
   * (no email), `canEditConnections` is `true` (no org config), and
   * `isSubCarrier`/`isTerminalOrg`/`isUTASOnlyOrg` are all `false`. Each of
   * those defaults is the *permissive* one, so anything deciding whether to
   * put UI in front of a user must wait for this rather than for `loggedIn`.
   */
  const sessionReady = computed(() => userInfo.value !== null)
  const useOwnChassis = computed(() => {
    if (currentOrg.value?.config.defaultChassisType !== undefined) {
      return currentOrg.value?.config.defaultChassisType === 'own'
    } else if (useOwnChassisLastUsedValue.value !== undefined) {
      return useOwnChassisLastUsedValue.value
    } else {
      return false // Default to pool
    }
  })

  const userEmail = computed(() => {
    return userInfo.value?.email ?? null
  })
  const is_internal_user = computed(() => {
    if (userEmail.value) {
      return isInternalUser(userEmail.value)
    } else {
      return false
    }
  })

  // Actions
  function setUseOwnChassisLastUsedValue(value: boolean) {
    useOwnChassisLastUsedValue.value = value
  }
  function setAuthHeader(header: string) {
    if (header === authHeader.value) return
    authHeader.value = header
    setAxiosAuthorizationHeader(header)
    loggedIn.value = true
    identifyUserWithServices()
  }
  function setAndSaveAuthHeader(header: string, orgID: number) {
    if (header === authHeader.value) return
    setAuthHeader(header)
    authRepo.saveAuthHeader({ header, orgID })
  }
  function loadAuth() {
    const authHeader = authRepo.loadAuthHeader()
    if (authHeader) {
      setAuthHeader(authHeader.header)
    } else {
      // TODO
    }
  }
  loadAuth()

  function addAuthHeader(tokenInfo: GETToken) {
    const authHeaderVal = makeAuthHeader(tokenInfo)
    authHeadersByOrgID.value[tokenInfo.organization_id] = authHeaderVal
  }
  function loadInfoFromAPI(): Promise<UserInfo> {
    if (loadInfoPromise.value) {
      return loadInfoPromise.value
    }
    const api = new UsersApi()
    const usedHeader = getAxiosAuthorizationHeader()
    if (!usedHeader) throw new Error('User is not logged in')
    loadInfoPromise.value = api
      .getCurrentUserInfoUsersCurrentInfoGet()
      .then((resp) => {
        const sentHeader = getAxiosAuthorizationHeader()
        if (sentHeader !== usedHeader) {
          throw new Error('Current user changed while loading user info')
        }
        return updateUserAndSaveToStorage(resp.data)
      })
      .finally(() => {
        loadInfoPromise.value = null
      })

    return loadInfoPromise.value
  }
  function updateUser(user: GETUserInfo): UserInfo {
    currentOrg.value = new Organization(user.current_organization)
    userInfo.value = new UserInfo(user, demo_mode.value)
    feature_flags.value = new Set<string>(userInfo.value.feature_flags)
    orgNotificationsChannel.value = `private-organization-notifications-${userInfo.value.current_organization.id}`
    userNotificationsChannel.value = `private-user-notifications-${userInfo.value.current_organization.id}-${userInfo.value.id}`
    readyToConnectToWebSocket.value = true
    // We just got new user data so we must be done with our our change
    clearOrgChangeState()

    return userInfo.value
  }
  async function updateUserAndSaveToStorage(
    user: GETUserInfo
  ): Promise<UserInfo> {
    const newUserInfo = updateUser(user)
    await userInfoRepo.saveToStorage(user)
    return newUserInfo
  }

  /**
   * Loads user info from IndexDB storage (which supports large objects, which we have for
   * internal Dray Dog users) if available
   */

  async function loadInfoIfNeeded(): Promise<UserInfo> {
    if (userInfo.value) {
      return userInfo.value
    }
    const cachedUserInfo = await userInfoRepo.loadFromStorage()
    let loadInfoPromise: Promise<UserInfo> | null = null
    if (cachedUserInfo.shouldRefresh) {
      loadInfoPromise = loadInfoFromAPI()
    }
    if (cachedUserInfo.userInfo) {
      return updateUser(cachedUserInfo.userInfo)
    } else if (!loadInfoPromise) {
      loadInfoPromise = loadInfoFromAPI()
    }
    return await loadInfoPromise
  }

  function updateOrg(orgUpdate: GETOrganization) {
    if (userInfo.value) {
      if (userInfo.value.current_organization.id === orgUpdate.id) {
        userInfo.value.current_organization = new Organization(orgUpdate)
      }
      for (const [index, org] of userInfo.value.organizations.entries()) {
        if (org.id === orgUpdate.id) {
          userInfo.value.organizations[index] = org
        }
      }
    }
  }

  /**
   * `currentOrg` is a `shallowRef`, so in-place mutations of the org model (e.g.
   * Auto Book profile CRUD editing `config.autoBookPresets`) don't notify reactive
   * consumers. Call this after such a mutation to force them to re-read.
   */
  function refreshCurrentOrg() {
    triggerRef(currentOrg)
  }

  async function switchToOrg(orgID: number): Promise<void> {
    setOrgChangeState() // So other tabs know we're changing orgs
    clearWebSocketChannels()
    if (!authHeadersByOrgID.value[orgID]) {
      const api = new UsersApi()
      const resp = await api.getTokenViaTokenUsersTokenViaTokenPost({
        organization_id: orgID,
      })
      authHeadersByOrgID.value[orgID] = makeAuthHeader(resp.data)
    }
    clearLocalStorageExcept(ALL_USER_DATA_KEYS)
    setAndSaveAuthHeader(authHeadersByOrgID.value[orgID], orgID)
    if (!userInfo.value) throw new Error('User info not loaded')
    const newUserInfo = changeOrgInUserInfo(userInfo.value.raw, orgID)
    if (newUserInfo.current_organization.id !== orgID) {
      throw new Error('Organization ID mismatch')
    }
    await updateUserAndSaveToStorage(newUserInfo)
    // Notify other tabs to reload before reloading this tab
    try {
      // Try catch because some old browsers don't support BroadcastChannel
      broadcastOrgChangeToOtherTabs(newUserInfo.current_organization.id)
    } catch (e) {
      console.error('Error broadcasting org change to other tabs', e)
      Sentry.captureException(e)
    }
    reloadPage()
  }

  async function login(tokenResp: GETToken) {
    const authHeader = makeAuthHeader(tokenResp)
    setAndSaveAuthHeader(authHeader, tokenResp.organization_id)
    demo_mode.value = false
    await loadInfoFromAPI()
  }
  async function loginUsingPortOptimizerToken(token: string): Promise<void> {
    const api = new UsersApi()
    // skipErrorHandling: the UTAS login page owns its own error UI for this
    // call — without this flag the global interceptor would also fire a toast,
    // producing duplicate feedback on failure.
    return await api
      .getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost(
        { token },
        { skipErrorHandling: true }
      )
      .then((resp) => {
        return login(resp.data)
      })
  }
  function clearWebSocketChannels() {
    readyToConnectToWebSocket.value = false
    orgNotificationsChannel.value = ''
    userNotificationsChannel.value = ''
  }

  function clearAllUserData() {
    clearWebSocketChannels()
    authRepo.saveAuthHeader(undefined)
    userInfo.value = null
    authHeader.value = null
    loggedIn.value = false
    clearLocalStorageExcept(GLOBAL_PREFERENCE_KEYS)
    feature_flags.value = new Set<string>()
    userInfoRepo.clearFromStorage()
  }

  function logout() {
    clearAxiosAuthorizationHeader()
    clearAllUserData()
    clearChatData()
    // @ts-expect-error: PostHog is injected at runtime
    if (window.posthog) {
      // @ts-expect-error: PostHog is injected at runtime
      posthog.reset()
    }
  }

  /**
   * Issue #451 / DEV-7272 defense-in-depth: if a stale session is authenticated
   * as a different email than the one the user is now acting as, clear it so the
   * stale identity can't be evaluated against the wrong user (e.g. the backend
   * deactivation guard). `userEmail` may be null if userInfo hasn't loaded yet —
   * treat that as a mismatch and clear, matching the existing signup behavior.
   *
   * If we don't know the target email we can't tell whether the session belongs
   * to a different user, so we leave a valid session alone rather than guess.
   */
  function clearStaleSessionForDifferentEmail(
    enteredEmail: string | null | undefined
  ) {
    if (!enteredEmail) return
    if (loggedIn.value && userEmail.value !== enteredEmail) {
      logout()
    }
  }
  const userAlreadyIdentified = ref(false)
  async function identifyUserWithServices() {
    if (!userInfo.value) return
    // Avoid duplicate calls to these services. If our page is refreshed, this would
    // be called again.
    if (userAlreadyIdentified.value) return
    const email = userInfo.value?.email
    if (import.meta.env.DEV) {
      return
    }
    configureUserWithCrispChat(userInfo.value)
    if (isInternalUser(email)) {
      return
    }
    const openReplayModule = await import('~/observability/openReplay')
    const openReplay = openReplayModule.getOpenReplayTracker()
    openReplay.setUserID(email)
    startOpenReplayTracking().then(() => {
      openReplay.setUserID(email)
    })
    // Identify in PostHog. PostHog is not available on localhost
    // @ts-expect-error: PostHog is injected at runtime
    if (window.posthog) {
      // Delay is to give PostHog time to initialize
      setTimeout(() => {
        // @ts-expect-error: PostHog is injected at runtime
        posthog.identify(email, {
          properties: {
            email,
          },
        })
      }, 2000)
    } else {
      console.log('PostHog not available')
    }
    // Identify in Sentry (error tracking)
    Sentry.setUser({ email })
    userAlreadyIdentified.value = true
  }

  function setShowHideImportAppt(value: boolean | undefined) {
    showHideImportAppointments.value = value
  }

  // Getters
  const token = computed((): string | undefined => {
    if (authHeader.value) {
      return authHeader.value.split(' ')[1]
    }
    return undefined
  })
  const currentRole = computed((): UserRoleType | null => {
    return userInfo.value?.current_organization.role ?? null
  })
  const isSubCarrier = computed((): boolean => {
    return currentRole.value === UserRoleType.SubCarrier
  })
  const hasManagementPermissions = computed((): boolean => {
    return (
      currentRole.value === UserRoleType.Management ||
      (userEmail.value !== null && isInternalUser(userEmail.value))
    )
  })
  const canEditConnections = computed((): boolean => {
    // If setting is off, everyone can edit
    if (!currentOrg.value?.config.onlyManagementUsersCanEditConnections) {
      return true
    }
    // If setting is on, only management can edit
    return hasManagementPermissions.value
  })
  const loggedInUser = computed((): SimpleUser | null => {
    if (userInfo.value) {
      return {
        id: userInfo.value.id,
        first_name: userInfo.value.first_name,
        last_name: userInfo.value.last_name,
      }
    }
    return null
  })

  const isTerminalOrg = computed((): boolean => {
    if (userInfo.value) {
      // if its a terminal subscription
      const terminal_org =
        userInfo.value.current_organization.subscriptions.includes(
          OrganizationSubscription.Terminal
        )
      return terminal_org
    }
    return false
  })

  const isDrayDogOrg = computed((): boolean => {
    return (
      userInfo.value?.current_organization.subscriptions.includes(
        OrganizationSubscription.DrayDogMain
      ) ||
      userInfo.value?.current_organization.subscriptions.includes(
        OrganizationSubscription.DrayDogOakland
      ) ||
      false
    )
  })

  const isLaLBOrg = computed((): boolean => {
    return (
      userInfo.value?.current_organization.subscriptions.includes(
        OrganizationSubscription.DrayDogMain
      ) || false
    )
  })

  const isOaklandOrg = computed((): boolean => {
    return (
      userInfo.value?.current_organization.subscriptions.includes(
        OrganizationSubscription.DrayDogOakland
      ) || false
    )
  })
  const defaultPortComplexForNewContainers = computed((): PortComplex => {
    // Default to Oakland only if the org is an Oakland org and not an LA/LB org.
    if (isOaklandOrg.value && !isLaLBOrg.value) {
      return PortComplex.Oak
    }

    // Otherwise, default to LA/LB. This covers LA/LB-only, UAS-only, and dual-port orgs.
    return PortComplex.LaLb
  })
  const addContainersPortSelectorLastUsedValue = useLocalStorage<PortComplex>(
    ADD_CONTAINERS_PORT_SELECTOR_LAST_USED_VALUE_KEY,
    defaultPortComplexForNewContainers.value
  )

  const isUTASOrg = computed((): boolean => {
    if (userInfo.value) {
      // if its a UAS subscription
      const utas_org =
        userInfo.value.current_organization.subscriptions.includes(
          OrganizationSubscription.Uas
        )
      return utas_org
    }
    return false
  })
  // WARNING: unreliable at mount time. `userInfo` hydrates asynchronously from
  // IndexedDB after `loggedIn` is already true (see "Auth & Session Lifecycle" in
  // frontend/CLAUDE.md), so this returns `false` during the hydration window even
  // for UTAS-only orgs. Don't use it to gate side effects that fire on mount —
  // wait on `userInfo` directly, or don't rely on the guard at all if you know
  // the app is UTAS-only by construction.
  const isUTASOnlyOrg = computed((): boolean => {
    const org = userInfo.value?.current_organization
    return org ? orgIsUTASOnly(org) : false
  })
  // Whether the user belongs to any org that ISN'T UTAS-only, i.e. somewhere in the
  // main app they could return to. Used to decide whether a user stranded on a
  // UTAS-only org should be offered an escape instead of a silent redirect to UTAS.
  const userHasNonUtasOrg = computed((): boolean => {
    return (
      userInfo.value?.organizations.some((org) => !orgIsUTASOnly(org)) ?? false
    )
  })

  const getShowHideImportAppt = computed((): boolean | undefined => {
    return showHideImportAppointments.value
  })

  // Terminal filtering logic
  const availableTerminals = computed(() => {
    const terminalList = []
    if (isLaLBOrg.value || isUTASOrg.value || isTerminalOrg.value) {
      terminalList.push(...LA_LB_TERMINALS)
    }
    if (isOaklandOrg.value) {
      terminalList.push(...OAKLAND_TERMINALS)
    }
    return terminalList
  })

  const availablePortComplexes = computed(() => {
    const portComplexSet: Set<PortComplex> = new Set()
    if (isLaLBOrg.value) {
      portComplexSet.add(PortComplex.LaLb)
    }
    if (isOaklandOrg.value) {
      portComplexSet.add(PortComplex.Oak)
    }
    if (isUTASOrg.value) {
      portComplexSet.add(PortComplex.LaLb)
    }
    if (isTerminalOrg.value) {
      portComplexSet.add(PortComplex.LaLb)
    }
    return Array.from(portComplexSet)
  })

  const availableTerminalNames = computed(() => {
    return availableTerminals.value.map((t) => t.name)
  })

  const activatedTerminals = computed(() => {
    return new Set(availableTerminalNames.value)
  })

  /**
   * Whether any terminal this org works runs a green lane / clean truck program.
   *
   * Gates the green/EV *filters*, which are pointless where no terminal has a green
   * lane. Deliberately org-level rather than keyed off whichever terminal is currently
   * selected: the green leaf badge is never terminal-gated either (it renders wherever
   * a truck is confirmed green), and a filter that appeared and vanished as the terminal
   * filter changed would be worse than one that occasionally returns nothing.
   */
  const hasGreenLaneTerminal = computed(() => {
    return availableTerminalNames.value.some((terminal) =>
      TERMINALS_WITH_GREEN_LANE.has(terminal)
    )
  })

  function getTerminalsForAppointments(showOnlyUtasTerminals: boolean) {
    if (showOnlyUtasTerminals) {
      return UTAS_ACTIVE_TERMINALS.sort((a, b) => a.localeCompare(b))
    }
    const { terminalMatchesPortComplexes } = useGlobalFilters()
    const terminals = Array.from(TERMINALS_REQUIRING_APPOINTMENTS).filter(
      (terminal) => {
        return terminalMatchesPortComplexes(terminal)
      }
    )
    return terminals.sort((a, b) => a.localeCompare(b))
  }

  function getTerminals() {
    const { terminalMatchesPortComplexes } = useGlobalFilters()
    //   filter activated terminals based on port complex
    return availableTerminals.value.filter((terminal) => {
      return terminalMatchesPortComplexes(terminal.name)
    })
  }

  // Setup flow functions
  async function handleSetupSkipped(skipType: 'later' | 'never') {
    if (!connectionSetupStatus.value) return

    const updatedProgress = { ...connectionSetupStatus.value }

    switch (skipType) {
      case 'later':
        updatedProgress.skip_until = new Date(
          Date.now() + 24 * 60 * 60 * 1000
        ).toISOString() // wait 1 day
        break
      case 'never':
        updatedProgress.permanently_skipped = true
        break
    }

    connectionSetupStatus.value = {
      ...updatedProgress,
      last_updated: new Date().toISOString(),
    }
  }

  const connectionSetupSkipped = computed<boolean>(() => {
    if (!connectionSetupStatus.value) return false
    if (connectionSetupStatus.value.permanently_skipped) return true
    if (
      connectionSetupStatus.value.skip_until &&
      fastParseISO(connectionSetupStatus.value.skip_until) > DateTime.now()
    ) {
      return true
    }
    return false
  })

  return {
    // Refs
    userEmail,
    authHeader,
    exportInTriniumMode,
    exportAllAppointments,
    useOwnChassis,
    loading,
    feature_flags,
    demo_mode,
    testMode,
    portComplexesLocalStorage,
    addContainersPortSelectorLastUsedValue,
    userInfo,
    sessionReady,
    is_internal_user,
    showHideImportAppointments,
    orgNotificationsChannel,
    userNotificationsChannel,
    // Actions
    addAuthHeader,
    switchToOrg,
    loadInfoIfNeeded,
    login,
    logout,
    clearStaleSessionForDifferentEmail,
    identifyUserWithServices,
    loadInfoFromAPI,
    updateUserAndSaveToStorage,
    updateOrg,
    refreshCurrentOrg,
    updateUser,
    setShowHideImportAppt,
    loginUsingPortOptimizerToken,
    setUseOwnChassisLastUsedValue,
    // Computed properties
    loggedIn,
    readyToConnectToWebSocket,
    token,
    isSubCarrier,
    hasManagementPermissions,
    canEditConnections,
    loggedInUser,
    currentOrg,
    getShowHideImportAppt,
    isTerminalOrg,
    isUTASOrg,
    isUTASOnlyOrg,
    userHasNonUtasOrg,
    isDrayDogOrg,
    isLaLBOrg,
    isOaklandOrg,
    defaultPortComplexForNewContainers,
    connectionSetupSkipped,
    // Setup flow
    handleSetupSkipped,
    // Terminal filtering
    availableTerminals,
    availableTerminalNames,
    activatedTerminals,
    hasGreenLaneTerminal,
    getTerminalsForAppointments,
    getTerminals,
    // Port filtering
    availablePortComplexes,
  }
})

function makeAuthHeader(tokenResp: GETToken): string {
  return `${tokenResp.token_type} ${tokenResp.access_token}`
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
/**
 * Reconfigures a user infor object so the current organization is different.
 *
 * This is much more efficient than making another API call to get the new user info
 *
 * @param userInfo - The current user information object
 * @param orgID - The ID of the organization to switch to
 * @returns A new user info object with the updated current organization
 * @throws Error if the specified organization ID is not found in the user's organizations
 */
function changeOrgInUserInfo(
  userInfo: GETUserInfo,
  orgID: number
): GETUserInfo {
  if (userInfo.current_organization.id === orgID) {
    return userInfo // Nothing to change
  }
  for (const org of userInfo.organizations) {
    if (org.id === orgID) {
      return {
        ...userInfo,
        current_organization: org,
      }
    }
  }
  throw new Error(`Organization with ID ${orgID} not found in user info`)
}
